import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  TableContainer,
  Table,
  Tr,
  Grid,
  GridItem,
  Tbody,
  Td,
  Thead,
  TableCaption,
} from '@chakra-ui/react';
import * as _ from 'lodash';

import {
  DistrictOutput,
  DistrictDemoData,
  UniverseType,
  OldQuoteGet,
  OldPricingQuotePrice,
} from '../../lib/pricing-tool-api';
import {
  IQuoteService,
  useQuoteService,
} from '../../lib/services/QuoteService';
import { LocationInformationTable } from '../../components/pricing/LocationInformationSection';
import { UNIVERSE_TYPE_OPTIONS } from '../../components/pricing/TargetUniverseSection';
import { formatManualMarkupForDisplay } from '../../components/pricing/FeeTypeSection';

interface SimpleDetailTableProps {
  data: Map<string, string | undefined>;
  title: string;
}

function SimpleDetailTable(props: SimpleDetailTableProps) {
  const { data, title } = props;
  return (
    <TableContainer>
      <Table size="sm">
        <TableCaption placement="top" bg="brand.primaryColor">
          {title}
        </TableCaption>
        <Tbody>
          {[...data.keys()].map((key) => (
            <Tr key={key}>
              <Td>{key}</Td>
              <Td whiteSpace="normal" overflow="auto">
                {data.get(key)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

interface PriceMatrixTableProps {
  title: string;
  sampleSizes: number[];
  numberOfQuestions: number[];
  prices: OldPricingQuotePrice[];
  valueFor: (p: OldPricingQuotePrice) => number;
}

function PriceMatrixTable({
  title,
  sampleSizes,
  numberOfQuestions,
  prices,
  valueFor,
}: PriceMatrixTableProps) {
  return (
    <TableContainer>
      <Table size="sm">
        <TableCaption placement="top" bg="brand.primaryColor">
          {title}
        </TableCaption>
        <Thead>
          <Tr>
            <Td fontWeight="bold">Respondents</Td>
            {sampleSizes.map((n) => (
              <Td fontWeight="bold" key={n}>
                n = {n}
              </Td>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {numberOfQuestions.map((questionCount) => (
            <Tr key={questionCount}>
              <Td>
                <strong>{questionCount} Question Poll</strong>
              </Td>
              {sampleSizes.map((sampleSize) => {
                const match = _.find(
                  prices,
                  (p) =>
                    p.number_of_questions === questionCount &&
                    p.sample_size === sampleSize,
                );
                return (
                  <Td key={`${questionCount}-${sampleSize}`}>
                    {match ? `$${valueFor(match).toLocaleString()}` : '-'}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

interface QuoteDescriptionProps {
  quote: OldQuoteGet | undefined;
}

function QuoteDescription(props: QuoteDescriptionProps) {
  const { quote } = props;

  const [districtDemoData] = useState<DistrictDemoData[]>(
    quote?.district_demo_data && quote?.district_demo_data.length > 0
      ? quote?.district_demo_data
      : [],
  );

  const clientInformation = () => {
    if (!quote) {
      throw new Error('Quote was null, contact the tech team');
    }
    return new Map<string, string | undefined>([
      ['ID', quote?.id],
      ['Client Type', quote.price_info.request.client_type],
      [
        'Business Unit',
        (quote.price_info.request as any)?.business_unit || 'N/A',
      ],
      ['Deal ID', quote?.zendesk_deal_id],
      ['Deal Name', quote?.deal_name],
      ['Client Name', quote?.client_name],
      ['Firewall Side', quote?.firewall_side as string],
      ['Memo', quote?.memo],
    ]);
  };

  const targetUniverseInfo = () => {
    const oversampleType = (
      quote?.price_info.request as any
    )?.oversample_type?.toString();
    return new Map<string, string | undefined>([
      [
        'Num of Universes',
        (quote?.price_info.request as any)?.num_of_universes?.toString() ||
          'N/A',
      ],
      [
        'Universe Types',
        (quote?.price_info.request.universe_types as UniverseType[])
          .map((u: UniverseType) => {
            const universeType = UNIVERSE_TYPE_OPTIONS.find(
              (ut) => ut.value === u,
            );
            return universeType ? universeType.choiceText : u.toString();
          })
          .join(', '),
      ],
      ['Oversample Type', oversampleType || 'N/A'],
      [
        'Special Demographic Filtering',
        `${
          ((quote?.price_info.request as any)?.special_demo_filter_pct || 0) *
          100
        }%`,
      ],
    ]);
  };

  const feeTypeInfo = () => {
    const discountType = (
      quote?.price_info.request as any
    )?.discount?.toString();
    const markup = formatManualMarkupForDisplay(
      (quote?.price_info.request as any)?.manual_markup as number,
    );
    const messageTesting = (
      quote?.price_info.request as any
    )?.message_testing?.toString();
    return new Map<string, string | undefined>([
      ['Discount', discountType || 'N/A'],
      ['ASAP', (quote?.price_info.request as any)?.asap?.toString() || 'N/A'],
      ['Mark-up (+) / Discount (-) Percentage', `${markup}%`],
      [
        'Additional fee(s)',
        `$${
          (quote?.price_info.request as any)?.additional_fees?.toString() || '0'
        }`,
      ],
      ['Message Testing', messageTesting || 'N/A'],
    ]);
  };

  const maxRecommendedSampleSize = () =>
    districtDemoData.length === 0
      ? Number.MAX_SAFE_INTEGER // this is a hack so that the table doesn't show a warning if demo data is missing
      : Number.MAX_SAFE_INTEGER; // simplified for old quotes

  const demoDataAsMap = () => {
    const result = new Map<string, DistrictDemoData>();
    districtDemoData.forEach((demo) => {
      const id =
        demo.district.id ||
        `${demo.district.state}|${demo.district.district_type}|${demo.district.name}`;
      result.set(id, demo);
    });

    return result;
  };

  return (
    <Box>
      <Grid templateColumns="repeat(2, 5fr)">
        <GridItem colSpan={2}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Old Quote (V1) - {quote?.deal_name}
          </Text>
          <Alert status="info" mb={4}>
            <AlertIcon />
            This is a read-only old quote. Use &quot;New Quote&quot; to create
            editable quotes.
          </Alert>
        </GridItem>
        <GridItem fontSize="md">
          <SimpleDetailTable
            data={clientInformation()}
            title="Client Information"
          />
        </GridItem>
        <GridItem fontSize="md">
          <SimpleDetailTable
            data={targetUniverseInfo()}
            title="Target Universe"
          />
        </GridItem>

        <GridItem fontSize="md">
          <SimpleDetailTable data={feeTypeInfo()} title="Fee Type" />
        </GridItem>
        <GridItem colSpan={2}>
          <Text bg="brand.primaryColor" fontSize="md">
            Location Information
          </Text>
          <LocationInformationTable
            selectedDistricts={
              quote?.price_info.request.districts as DistrictOutput[]
            }
            selectedDistrictsDemoData={demoDataAsMap()}
            onRemoveDistrict={() => {}}
            showRemoveButton={false}
            allowNullDemoData
            size="sm"
          />
        </GridItem>
      </Grid>

      <Text bg="brand.primaryColor" mb={4}>
        Prices
      </Text>
      <Grid templateColumns="repeat(2, 1fr)" gap="20px">
        <GridItem>
          <PriceMatrixTable
            title="Estimated Recruitment Cost"
            sampleSizes={quote?.price_info.request.sample_sizes ?? []}
            numberOfQuestions={
              quote?.price_info.request.number_of_questions ?? []
            }
            prices={
              (quote?.price_info.prices as OldPricingQuotePrice[]) ?? []
            }
            valueFor={(p) => p.recruitment_cost}
          />
        </GridItem>

        <GridItem>
          <PriceMatrixTable
            title="Quote"
            sampleSizes={quote?.price_info.request.sample_sizes ?? []}
            numberOfQuestions={
              quote?.price_info.request.number_of_questions ?? []
            }
            prices={
              (quote?.price_info.prices as OldPricingQuotePrice[]) ?? []
            }
            valueFor={(p) => p.price}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <TableContainer>
            <Table size="sm">
              <TableCaption placement="top" bg="brand.primaryColor">
                Proposal List
              </TableCaption>
              <Tbody>
                <Tr>
                  <Td fontWeight="bold">DESCRIPTION</Td>
                  <Td fontWeight="bold">PRICE</Td>
                </Tr>
                {((quote?.price_info.prices as OldPricingQuotePrice[]) ?? []).map(
                  (p) => (
                    <Tr key={`${p.sample_size}-${p.number_of_questions}`}>
                      <Td>
                        {p.sample_size} respondent, {p.number_of_questions}{' '}
                        question poll
                      </Td>
                      <Td>${p.price.toLocaleString()}</Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </GridItem>
      </Grid>
    </Box>
  );
}

function QuoteDetail() {
  const { quoteId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [quote, setQuote] = useState<OldQuoteGet>();
  const [errMsg, setErrMsg] = useState<string>();
  const quoteService = useQuoteService();

  const fetchSavedQuote = async (api: IQuoteService, id: string) => {
    const response = await api.getQuote(id, true);
    return response;
  };

  useEffect(() => {
    if (!quoteId) {
      return;
    }
    fetchSavedQuote(quoteService, quoteId)
      .then((response) => {
        setQuote(response.data);
        setIsLoading(false);
      })
      .catch(() => {
        setErrMsg(
          `Could not fetch quote with ID ${quoteId}. Please contact the tech team with any questions.`,
        );
        setIsLoading(false);
      });
  }, [quoteId, quoteService]);

  return (
    <Box>
      {isLoading ? (
        <Spinner />
      ) : (
        <Box>
          {!errMsg ? (
            <QuoteDescription quote={quote} />
          ) : (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Fetch Error</AlertTitle>
              <AlertDescription>{errMsg}</AlertDescription>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}

export default QuoteDetail;
